import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, CalendarCheck } from "lucide-react";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Services & Rates", to: "/services-and-rates" },
  { label: "Care Process", to: "/care-process" },
  { label: "Why Choose Us", to: "/why-choose-us" },
  { label: "Patient Referral", to: "/referral" },
  { label: "Contact Us", to: "/contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);

  const linkCls = (isActive) =>
    `whitespace-nowrap text-[15px] font-semibold transition-colors duration-300 ${
      isActive ? "text-brand-teal" : "text-slate-600 hover:text-brand-teal"
    }`;

  return (
    <header data-testid="site-header" className="fixed top-0 inset-x-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200/60">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-8 h-[84px] flex items-center justify-between gap-6">
        <Link to="/" onClick={() => setOpen(false)} data-testid="header-logo-link" className="flex items-center gap-3 shrink-0">
          <img src="/assets/bms-logo.png" alt="BMS Health Care Services logo" className="h-14 w-auto" />
          <span className="hidden sm:flex xl:hidden 2xl:flex flex-col justify-center leading-tight whitespace-nowrap">
            <span className="font-display font-bold text-brand-navy text-lg tracking-tight">BMS Health Care</span>
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-teal">Compassion · Care · Commitment</span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-5" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => linkCls(isActive)}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <Link
            to="/contact"
            data-testid="header-consultation-cta"
            className="hidden md:inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-brand-teal px-5 py-2.5 text-[15px] font-bold text-white shadow-[0_8px_24px_rgba(0,128,128,0.35)] hover:bg-brand-navy transition-colors duration-300 active:scale-95"
          >
            <CalendarCheck className="h-4 w-4" strokeWidth={1.8} />
            Book a Free Care Consultation
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            className="xl:hidden p-2 rounded-lg text-brand-navy hover:bg-brand-mist transition-colors duration-300"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="xl:hidden overflow-hidden bg-white border-b border-slate-200"
            aria-label="Mobile"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {NAV.map((item) => (
                <NavLink
                  key={item.label}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `py-3 px-3 rounded-lg text-base font-semibold transition-colors duration-300 ${
                      isActive ? "bg-brand-mist text-brand-teal" : "text-brand-navy hover:bg-brand-mist"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-consultation-cta"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-brand-teal px-5 py-3 text-base font-bold text-white active:scale-95 transition-colors duration-300"
              >
                <CalendarCheck className="h-4 w-4" strokeWidth={1.8} />
                Book a Free Care Consultation
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
